import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { IntlProvider } from "react-intl";
import itMessages from "./i18n/it_IT.json";
import enMessages from "./i18n/en_US.json";
import frMessages from "./i18n/fr_FR.json";
import deMessages from "./i18n/de_DE.json";
import esMessages from "./i18n/es_ES.json";
import { expandMessagesForIntl } from "./i18n/expandMessagesForIntl";
import BrowserPersistence from "@overrides_venia/peregrine/util/simplePersistence";
const availableLanguages = ["it", "en", "fr", "de", "es"];
const messagesMap = {
  "it": expandMessagesForIntl(itMessages),
  "en": expandMessagesForIntl(enMessages),
  "fr": expandMessagesForIntl(frMessages),
  "de": expandMessagesForIntl(deMessages),
  "es": expandMessagesForIntl(esMessages)
};
const lsLanguageMapping = {
  "it": "ITA",
  "en": "ENU",
  "fr": "FRA",
  "de": "DEU",
  "es": "ESP"
};
export function getProductSuffix() {
  return ".html";
}
export function getCategorySuffix() {
  return ".html";
}
const LocaleContext = createContext(null);
export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}
const LocaleProvider = _ref => {
  let {
    initialLocale,
    children
  } = _ref;
  const browserLocale = typeof navigator !== "undefined" ? navigator.language : "it-IT";
  const storage = new BrowserPersistence();
  const browserLanguageCode = useMemo(() => browserLocale.split("-")[0], [browserLocale]);
  const websiteLanguage = availableLanguages.includes(browserLanguageCode) ? browserLanguageCode : "en";
  const [language, setLocalLanguage] = useState(websiteLanguage);
  const [lsLanguage, setLsLanguage] = useState(lsLanguageMapping[websiteLanguage] || "ITA");
  const [missingTranslationList, setMissingTranslationList] = useState([]);
  const messages = useMemo(() => {
    return messagesMap[language] || messagesMap.it;
  }, [language]);
  const onIntlError = error => {
    if (error.code === "MISSING_TRANSLATION") {
      if (!missingTranslationList.includes(error.descriptor.id)) {
        var _exponea;
        console.warn("Missing translation", error);
        setMissingTranslationList(prevList => [...prevList, error.descriptor.id]);
        if ((_exponea = exponea) !== null && _exponea !== void 0 && _exponea.track) {
          exponea.track("missing_translation", {
            message_id: error.descriptor.id,
            language
          });
        }
      }
      return;
    }
    throw error;
  };
  useEffect(() => {
    storage.setItem("language_code", websiteLanguage);
  }, []);
  const setLanguage = lang => {
    setLocalLanguage(lang);
    storage.setItem("language_code", lang);
    setLsLanguage(lsLanguageMapping[lang] || "ITA");
  };
  const value = useMemo(() => ({
    language,
    setLanguage,
    availableLanguages,
    lsLanguage
  }), [language]);
  return /*#__PURE__*/React.createElement(LocaleContext.Provider, {
    value: value
  }, /*#__PURE__*/React.createElement(IntlProvider, {
    key: language // WHY: force remount when locale changes
    ,
    locale: language,
    defaultLocale: "it-IT",
    messages: messages,
    onError: onIntlError
  }, children));
};
export function getLanguage() {
  const language = navigator.languages.includes("it-IT") || navigator.languages.includes("it") ? "IT" : "EN";
  return language;
}
export default LocaleProvider;